import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AudioLayout from "../AudioLayout";
import { audioApi } from "../api";
import { useAudioAuth } from "../AudioAuthContext";
import { useAudioAccess } from "../AudioAccessContext";
import PlayPaywallModal from "../components/PlayPaywallModal";
import usePlayPaywall from "../usePlayPaywall";
import { BookOpen, PlayCircle, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";

export default function AudioHome() {
  const { user, subscribed } = useAudioAuth();
  const access = useAudioAccess();
  const [books, setBooks] = useState([]);
  const paywall = usePlayPaywall({ defaultNext: "/audio-comics/books" });
  // In FREE mode nobody sees the pricing banner; in PAID mode guests + logged-out users do.
  const showPricingBanner = !access.is_free && (!user || !subscribed);

  useEffect(() => {
    audioApi.get("/books").then(r => setBooks(r.data)).catch(() => {});
  }, []);

  return (
    <AudioLayout>
      <section className="relative pb-12">
        <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full bg-fuchsia-500/20 blur-3xl pointer-events-none"/>
        <div className="absolute top-20 right-0 w-72 h-72 rounded-full bg-amber-400/15 blur-3xl pointer-events-none"/>
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-4">Listen · Imagine · Grow</div>
          <h1 className="font-bold text-4xl sm:text-5xl md:text-6xl leading-tight tracking-tight max-w-3xl">
            Basavanna&apos;s vachanas, told as <span className="text-amber-300">audio comics</span> for children.
          </h1>
          <p className="mt-5 text-white/70 max-w-xl text-base sm:text-lg leading-relaxed">
            Books and vachanas in voices children love. Tap a story, press play, watch the wisdom unfold.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to="/audio-comics/books" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-400 text-black font-semibold hover:bg-amber-300" data-testid="ac-cta-library">
              <BookOpen size={16}/> {access.cta_primary} <ArrowRight size={14}/>
            </Link>
            {!access.is_free && !subscribed && (
              <button
                onClick={() => paywall.promptPaywall("/audio-comics/subscription")}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-fuchsia-500/20 hover:bg-fuchsia-500/30 border border-fuchsia-400/40 text-fuchsia-200"
                data-testid="ac-cta-subscribe-hero"
              >
                <Sparkles size={14}/> {access.cta_secondary}
              </button>
            )}
          </div>
        </div>
      </section>

      {access.is_free && access.launch_offer_enabled && (
        <section
          className="mb-10 rounded-2xl border border-emerald-400/30 bg-gradient-to-r from-emerald-400/[0.10] via-amber-400/[0.06] to-transparent p-5 sm:p-6 flex flex-wrap items-center justify-between gap-5"
          data-testid="ac-launch-banner"
        >
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-300 mb-1">Launch Offer</div>
            <div className="text-lg sm:text-2xl font-semibold leading-tight" data-testid="ac-launch-banner-text">
              {access.launch_offer_text}
            </div>
            <p className="mt-2 text-sm text-white/60">Everything on Bala Basava Audio Comics is currently free to listen — no sign-in, no subscription.</p>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2 shrink-0">
            <div className="text-right">
              <div className="text-3xl sm:text-4xl font-bold leading-none text-emerald-300" data-testid="ac-pricing-free">FREE</div>
              <div className="text-[11px] uppercase tracking-widest text-white/45 mt-1">{access.cta_primary}</div>
            </div>
            <Link
              to="/audio-comics/books"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-black font-semibold text-sm hover:bg-amber-300"
              data-testid="ac-pricing-cta"
            >
              <BookOpen size={14}/> {access.cta_primary}
            </Link>
          </div>
        </section>
      )}

      {showPricingBanner && (
        <section
          className="mb-10 rounded-2xl border border-amber-400/30 bg-gradient-to-r from-amber-400/[0.08] via-fuchsia-500/[0.06] to-transparent p-5 sm:p-6 flex flex-wrap items-center justify-between gap-5"
          data-testid="ac-pricing-banner"
        >
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-1">Explore freely · Subscribe to play</div>
            <div className="text-lg sm:text-xl font-semibold leading-tight">
              Explore all Bala Basava Audio Comics before subscribing.
            </div>
            <ul className="mt-3 text-sm text-white/65 grid sm:grid-cols-2 gap-x-6 gap-y-1">
              <li className="inline-flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-300"/> Unlimited browsing</li>
              <li className="inline-flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-300"/> {books.length} Books</li>
              <li className="inline-flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-300"/> View thumbnails &amp; descriptions</li>
              <li className="inline-flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-300"/> Cancel anytime</li>
            </ul>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2 shrink-0">
            <div className="text-right">
              <div className="text-3xl sm:text-4xl font-bold leading-none" data-testid="ac-pricing-paid">
                {access.currency_symbol}{access.subscription_price}
                <span className="text-base text-white/55">/mo</span>
              </div>
              <div className="text-[11px] uppercase tracking-widest text-white/45 mt-1">{access.plan_name}</div>
            </div>
            <Link
              to="/audio-comics/subscription"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-black font-semibold text-sm hover:bg-amber-300"
              data-testid="ac-pricing-cta"
            >
              <Sparkles size={14}/> {access.cta_secondary}
            </Link>
          </div>
        </section>
      )}

      <section className="mt-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-semibold">Featured books</h2>
          <Link to="/audio-comics/books" className="text-xs text-amber-300 hover:text-amber-200">View all →</Link>
        </div>
        {books.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-10 text-center text-white/60" data-testid="ac-empty-books">
            No audio comics available yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
            {books.slice(0, 5).map((b) => (
              <Link
                key={b.book_id}
                to={`/audio-comics/books/${b.book_id}`}
                className="group"
                data-testid={`ac-home-book-${b.book_id}`}
              >
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-white/5 ring-1 ring-white/10 group-hover:ring-amber-400/40 transition relative">
                  {b.thumbnail_url ? (
                    <img src={b.thumbnail_url} alt="" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="grid place-items-center h-full text-white/40"><PlayCircle size={32}/></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"/>
                  <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition">
                    <PlayCircle size={42} className="text-white drop-shadow-lg"/>
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <div className="text-[10px] text-amber-300 uppercase tracking-widest">{b.vachana_count} vachanas</div>
                    <div className="text-sm font-medium leading-tight line-clamp-2">{b.title}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <PlayPaywallModal
        open={paywall.open}
        onClose={paywall.close}
        onSignIn={paywall.goSignIn}
        onSubscribe={paywall.goSubscribe}
      />
    </AudioLayout>
  );
}
